import moment from 'moment';
import numeral from 'numeral';
import { HYPHEN_SYMBOL } from 'common/constants/general';
import { gridShortDate, isValidDate as isValidDateFunc } from 'utilities';

const containsLetter = (s: string) => s.match(/[a-zA-Z][^xX]+/g);
const containsNumber = (s: string) => s.match(/\d+/g);
const containsPercentage = (s: string) => s.endsWith('%');
const containsSlashesOrDashes = (s: string) => /[/-]/.test(s);
const containsDot = (s: string) => /\./.test(s);
const containsComma = (s: string) => /,/.test(s);
const containsDollar = (s: string) => /^\$/.test(s);
const containsX = (s: string) => /[xX]/.test(s);
const containsSpace = (s: string) => /\s/.test(s);
const containsReturn = (s: string) => /\r/.test(s);

// Note: Extracted dots and commas handlers to separate functions to reduce the cognitive complexity of handleDotSeparator.

/** Handles string with only dots: thousands vs decimal. */
const handleDotsOnly = (parts: string[]): string => {
  const lastPart = parts.at(-1);
  if (lastPart === '0' || lastPart === '00') {
    return parts.slice(0, -1).join('');
  }
  if (lastPart && lastPart.length <= 2 && parts.length > 1) {
    return `${parts.slice(0, -1).join('')}.${lastPart}`;
  }
  return parts.join('');
};

/** Handles string with only commas: decimal vs thousands. */
const handleCommasOnly = (sanitizedString: string): string => {
  const commaParts = sanitizedString.split(',');
  const lastPart = commaParts.at(-1);
  if (lastPart && lastPart.length <= 2 && commaParts.length === 2) {
    return commaParts.join('.');
  }
  return sanitizedString.split(',').join('');
};

const handleDotSeparator = (stringParam: string) => {
  /**
   * This function handles the case where the number has a dot as a decimal separator.
   * If the number is a multiple of 1000, it returns the number without the dot.
   * If the number is not a multiple of 1000, it returns the number.
   *
   * @param {String} stringParam - A string representing a number with a dot as a decimal separator.
   *
   * Test cases:
   * 1. '1.000' -> '1000'
   * 2. '1.000.000' -> '1000000'
   * 3. '1.0001' -> '1.0001'
   * 4. '1.0' -> '1'
   * 5. '1.00' -> '1'
   */
  let sanitizedString = stringParam.trim().startsWith('$') ? stringParam.slice(1) : stringParam.trim();

  if (/(\.\.)|(,,)/.test(sanitizedString)) {
    throw new Error('Invalid number format: Multiple consecutive dots or commas.');
  }

  const parts = sanitizedString.split('.');

  if (sanitizedString.includes('.') && sanitizedString.includes(',')) {
    sanitizedString =
      sanitizedString.lastIndexOf('.') > sanitizedString.lastIndexOf(',')
        ? sanitizedString.split('.').join('').replace(',', '.')
        : sanitizedString.split(',').join('');
  } else if (sanitizedString.includes('.')) {
    sanitizedString = handleDotsOnly(parts);
  } else if (sanitizedString.includes(',')) {
    sanitizedString = handleCommasOnly(sanitizedString);
  }

  return sanitizedString;
};

const replaceNumberSeparators = (stringNumber: string) => {
  let decimalSeparator;
  let thousandsSeparator;
  let numberWithoutThousandsSeparator;
  let tmpStringNumber;

  if (containsReturn(stringNumber)) {
    tmpStringNumber = stringNumber.replaceAll('\r', '');
  } else {
    tmpStringNumber = stringNumber.trim();
  }

  // If it has decimal separator, check if the separator is a dot. If it is, return the number.
  if (containsDot(tmpStringNumber) && !containsComma(tmpStringNumber)) {
    return handleDotSeparator(tmpStringNumber);
  }

  // If it doesn't have decimals, only the thousands separator is removed
  if (!containsDot(tmpStringNumber) || !containsComma(tmpStringNumber)) {
    numberWithoutThousandsSeparator = tmpStringNumber.replaceAll('[.,]', '');
    return numberWithoutThousandsSeparator;
  }

  // Check if the last separator is a comma or a dot
  if (tmpStringNumber.lastIndexOf(',') > tmpStringNumber.lastIndexOf('.')) {
    decimalSeparator = ',';
    thousandsSeparator = '.';
  } else {
    decimalSeparator = '.';
    thousandsSeparator = ',';
  }

  // Remove thousand separators (dots or commas)
  numberWithoutThousandsSeparator = tmpStringNumber.replaceAll(`\\${thousandsSeparator}`, '');

  // Replace decimal separator with a dot
  const finalNumber = numberWithoutThousandsSeparator.replaceAll(`\\${decimalSeparator}`, '.');

  return finalNumber;
};

const sanitize = (s: string) => {
  let result = s;

  // Handle accounting-format negatives: (123) -> -123
  const trimmed = result.trim();
  if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
    result = `-${  trimmed.slice(1, -1)}`;
  }

  switch (true) {
    case containsX(result):
      return result.replaceAll('[xX]', '');
    case containsDollar(result) && (containsDot(result) || containsComma(result)):
      return replaceNumberSeparators(result);
    case containsSpace(result) && containsReturn(result):
      return result.replaceAll(/\r|\s/g, '');
    default:
      return result;
  }
};

const isValueNumber = (col: string) => {
  const satinizedCol = sanitize(col);
  const hasLetter = containsLetter(satinizedCol);
  const hasNumber = containsNumber(satinizedCol);
  return !(hasLetter && hasNumber);
};

const isHyphenSymbol = (col: string) => col?.trim() === HYPHEN_SYMBOL;

const parseValue = (col: string) => {
  if (col.trim() === '') {
    return '';
  }

  const hasLetter = containsLetter(col) && !containsX(col);
  const sanitizedCol = hasLetter ? col : sanitize(col);
  const hasPercentage = containsPercentage(sanitizedCol);
  const isNumber = isValueNumber(sanitizedCol);

  const isDate = containsSlashesOrDashes(sanitizedCol);
  const isDateFormatted = moment(sanitizedCol, 'MM/DD/YYYY', true).isValid();
  const formattedDate = isDateFormatted ? sanitizedCol : moment(sanitizedCol, 'DD/M/YYYY', true).format('MM/DD/YYYY');
  const dateToValidate = formattedDate === 'Invalid date' ? sanitizedCol : formattedDate;
  const isValidDate = isValidDateFunc(dateToValidate);

  // handles pasting 0.0% and having commas as decimal separator
  const percentify = numeral(sanitizedCol.replace(',', '.')).value() ?? 0;
  const numerify = numeral(sanitizedCol).value() ?? sanitizedCol;
  // Treat a trailing comma plus one or two digits as a decimal separator.
  // A thousands group (for example 1,500,000) must continue through numeral
  // so it is parsed as an actual numeric value.
  const isCommaFollowedByTwoNumbers = sanitizedCol.match(/,\d{1,2}%?$/g);

  if (isCommaFollowedByTwoNumbers) {
    const replacedComma = sanitizedCol.replace(',', '.');
    return replacedComma;
  }

  let value: string | number = sanitizedCol;
  if (isNumber) {
    value = numerify;
  }
  /* max number of decimal places for percentages in backend is 15,
  but when this gets parsed to decimal,
  it adds at least two 0s in the front for single digit percentages (e.g. 2.5% => 0.025),
  so to fixed should be 12 just to be safe */
  if (hasPercentage && isNumber) {
    value = percentify.toFixed(12);
  }
  if (isDate && isValidDate) {
    value = gridShortDate(dateToValidate);
  }

  return value;
};

// Copy/paste uses rows = newlines, cols = tabs. Empty cells become empty lines; row
// indices must be preserved on paste. removeIrrelevantTrailingEmptyRows drops only a lone
// EOF newline artifact ("100\n200\n"), not an intentional trailing blank ("100\n").
const removeIrrelevantTrailingEmptyRows = (rows: string[]) => {
  if (rows.length === 0 || rows.at(-1) !== '') {
    return rows;
  }

  let trailingEmptyCount = 0;
  while (trailingEmptyCount < rows.length && rows[rows.length - 1 - trailingEmptyCount] === '') {
    trailingEmptyCount += 1;
  }

  const contentRowCount = rows.length - trailingEmptyCount;

  // "100\n" → keep blank row; "100\n200\n" → drop artifact; "100\n\n" → keep both blanks.
  if (trailingEmptyCount === 1 && contentRowCount >= 2) {
    return rows.slice(0, -1);
  }

  return rows;
};

const pasteParser = (clipboard: string, maxAllowedCells: { maxAllowedRows: number; maxAllowedColumns: number } | null = null) => {
  const rows = removeIrrelevantTrailingEmptyRows(clipboard.split(/\r\n|\n|\r/));

  // First, parse the clipboard into its natural 2D shape.
  // Keep empty lines so pasted values land on the same rows as the copy source.
  const parsedClipboard = rows.map((row: string) => {
    const cols = row.split('\t');
    return cols.map((colValue: string) => {
      const col = isHyphenSymbol(colValue) ? '0' : colValue;
      return parseValue(col);
    });
  });

  // If we don't know the selection size, just return the parsed clipboard
  if (!maxAllowedCells?.maxAllowedRows || !maxAllowedCells?.maxAllowedColumns || !parsedClipboard) {
    return parsedClipboard;
  }

  const { maxAllowedRows, maxAllowedColumns } = maxAllowedCells;
  const values: (string | number)[][] = [];

  // Tile the clipboard values across the selected range (rows and columns)
  for (let r = 0; r < maxAllowedRows; r += 1) {
    const sourceRow = parsedClipboard[r % parsedClipboard.length] ?? parsedClipboard.at(-1);
    const rowValues = [];

    for (let c = 0; c < maxAllowedColumns; c += 1) {
      const sourceValue = sourceRow[c % sourceRow.length];
      rowValues.push(sourceValue);
    }

    values.push(rowValues);
  }

  return values;
};

export default pasteParser;
